import { Record as record, Map } from 'immutable';

import Railcard from '../Railcard';
import Discount from './Discount';
import { construct, fields, resolve } from '../../helpers/LinkMapper';
import {
    TicketRefundStates,
    RAILCARD1617_CODE,
    JOB_CENTRE_PLUS_RAILCARD_CODE,
    DisabledPeopleRailcards,
    ChangeoverActions,
} from '../../constants/AppConstants';
import TicketIssued from './TicketIssued';
import TicketType from '../TicketType';

const defaults = {
    adults: 0,
    children: 0,
    id: null,
    price: null,
    statusCode: null,
    discount: null,
    discountCode: null,
    discountAmount: null,
    passenger: null,
    railcard: new Railcard(),
    refundState: null,
    refunded: false,
    amended: false,
    printed: null,
    issued: null,
    attachment: null,
    rewardPoints: null,
    ticketType: null,
    ticketTypeOverride: null,
    travelStartDate: null,
    promoCode: null,
    changeoverAction: null,
};

class OrderTicket extends record(defaults) {
    static fromJSON (json) {
        return construct(OrderTicket, json, {
            adults: fields.number,
            children: fields.number,
            id: fields.number,
            passenger: resolve.with(Map),
            price: fields.number,
            statusCode: fields.string,
            discount: resolve.as(Discount),
            discountCode: fields.string,
            discountAmount: fields.number,
            railcard: resolve.as(Railcard),
            refundState: fields.string,
            refunded: fields.string,
            amended: fields.bool,
            printed: fields.bool,
            issued: resolve.as(TicketIssued),
            attachment: fields.string,
            rewardPoints: fields.number,
            ticketType: fields.string,
            ticketTypeOverride: resolve.as(TicketType),
            travelStartDate: fields.string,
            promoCode: fields.string,
            changeoverAction: fields.string,
        });
    }

    get isRefunded () {
        return this.refunded;
    }

    get isChangeoverRefund () {
        return this.changeoverAction === ChangeoverActions.REFUND;
    }

    get isRefundable () {
        return this.refundState === TicketRefundStates.REFUNDABLE;
    }

    canAmend() {
        return !this.amended && !this.isRefunded && !this.printed;
    }

    canRefundOffline() {
        return !(this.isRefunded || this.refundState === TicketRefundStates.EXCEEDED_VALIDITY);
    }

    isChildFlatFare () {
        return Boolean(this.ticketTypeOverride && this.ticketTypeOverride.code && ['CFF', 'CFS'].includes(this.ticketTypeOverride.code));
    }

    isChildTicket () {
        return Boolean(this.children);
    }

    isGroupSave () {
        return this.railcard && this.railcard.isGroupSave();
    }

    hasRailcard (railcard) {
        return this.railcard && this.railcard.code === railcard.code;
    }

    toComparable () {
        return this.delete('id');
    }

    hasPromoDiscount() {
        return this.discount && this.discount.isPromotional;
    }

    hasDiscount (discount) {
        return discount && this.discount && this.discount.code === discount.discountCode;
    }

    hasRailcardJCP () {
        return this.railcard && this.railcard.code === JOB_CENTRE_PLUS_RAILCARD_CODE;
    }

    hasRailcard1617 () {
        return this.railcard && (this.railcard.code === RAILCARD1617_CODE || this.railcard.code === JOB_CENTRE_PLUS_RAILCARD_CODE);
    }

    hasDisabledPeopleRailcard () {
        return this.railcard && DisabledPeopleRailcards.includes(this.railcard.code);
    }

    getTicketNumber() {
        return this.getIn(['issued', 'issuedTicketNumber']);
    }

    getSmartcardNumber() {
        return this.getIn(['passenger', 'smartcardNumber']);
    }

    getPhotocardNumber() {
        return this.getIn(['passenger', 'photocardNumber']);
    }

    getPassengerName() {
        return this.getIn(['passenger', 'name']);
    }

    getPromoCode() {
        return this.get('promoCode');
    }
}

export default OrderTicket;



// WEBPACK FOOTER //
// ./src/records/order/Ticket.js